import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { useEvaluateAnalysisPortfolio, useGetAnalysisQuestionnaireEvaluation } from "Infrastructure/ApiZfp/AnalyzerApi";
import { GetEvaluationNotFoundErrorCode } from "Infrastructure/ApiZfp/Schema";
import type { NsurePlanOverview } from "Infrastructure/ApiZfp/Schema/nsurePlanOverview";
import { PortfolioEvaluationItemEvaluationType } from "Infrastructure/ApiZfp/Schema/portfolioEvaluationItemEvaluationType";
import { PortfolioEvaluationItemPlanEvaluationType } from "Infrastructure/ApiZfp/Schema/portfolioEvaluationItemPlanEvaluationType";
import type { PortfolioEvaluationPlan } from "Infrastructure/ApiZfp/Schema/portfolioEvaluationPlan";
import { useContext, useEffect, useRef } from "react";
import type { NsureSelection } from "./InvestmentsNsureSelectionTypes";

const EVALUATION_ORDER: PortfolioEvaluationItemEvaluationType[] = [
  PortfolioEvaluationItemEvaluationType.InvestorType,
  PortfolioEvaluationItemEvaluationType.CustomerCategory,
  PortfolioEvaluationItemEvaluationType.RiskToleranceType,
  PortfolioEvaluationItemEvaluationType.LossCapabilityType,
  PortfolioEvaluationItemEvaluationType.InvestmentPreferenceType,
  PortfolioEvaluationItemEvaluationType.ESGType,
  PortfolioEvaluationItemEvaluationType.InvestmentHorizon,
];

export type NsureEvaluation = {
  readonly evaluationPlan: PortfolioEvaluationPlan | undefined;
  readonly items: PortfolioEvaluationItemEvaluationType[];
  readonly okCount: number;
  readonly hasWarning: boolean;
  readonly isPending: boolean;
  readonly isError: boolean;
  /** true until the result belongs to the currently selected portfolio / instrument */
  readonly isDataStale: boolean;
  readonly isQuestionnaireLoading: boolean;
  readonly isQuestionnaireNotFound: boolean;
  readonly isOk: (type: PortfolioEvaluationItemEvaluationType) => boolean;
};

export const EMPTY_NSURE_EVALUATION: NsureEvaluation = {
  evaluationPlan: undefined,
  items: [],
  okCount: 0,
  hasWarning: false,
  isPending: false,
  isError: false,
  isDataStale: false,
  isQuestionnaireLoading: false,
  isQuestionnaireNotFound: false,
  isOk: () => false,
};

/** Lives in `NsureSelectionProvider`, so the evaluation runs once per selection and survives step changes. */
export const useNsureEvaluation = (selection: NsureSelection, nsurePlan: NsurePlanOverview | undefined): NsureEvaluation => {
  const { analysisId, versionId } = useContext(AnalysisDetailContext);

  const { error, isLoading: isQuestionnaireLoading } = useGetAnalysisQuestionnaireEvaluation(analysisId, versionId, {
    query: { retry: 1 },
  });
  const isQuestionnaireNotFound = error?.code === GetEvaluationNotFoundErrorCode.evaluation_not_found;

  const { mutate, data, isPending, isError } = useEvaluateAnalysisPortfolio({
    // Evaluation errors (including 500) are shown inline on the item instead of the global toast.
    mutation: { onError: () => undefined },
  });
  const mutatedForSelectionRef = useRef<NsureSelection | undefined>(undefined);
  // Without a questionnaire evaluation nothing is ever sent, so the result cannot become stale.
  const isDataStale = !isQuestionnaireNotFound && mutatedForSelectionRef.current !== selection;

  useEffect(() => {
    if (!selection || isQuestionnaireLoading || isQuestionnaireNotFound) return;

    const oneTimePayment = nsurePlan?.oneTimePayment ?? 0;
    const periodicPayment = nsurePlan?.periodicPayment ?? 0;

    mutatedForSelectionRef.current = selection;
    mutate({
      analysisId,
      versionId,
      data: {
        externalID: nsurePlan?.nsureInvestmentPlanExternalId ?? undefined,
        investmentStrategies: selection.strategyInstrumentsEvaluation
          ? [
              {
                oneTimeParticipationInCurrency: oneTimePayment,
                periodicParticipationInCurrency: periodicPayment,
                oneTimeParticipationInPercentage: 100,
                periodicParticipationInPercentage: 100,
                strategyInstruments: selection.strategyInstrumentsEvaluation.map((si) => ({
                  instrumentID: si.id,
                  participationInPercentage: si.participationInPercentage,
                  oneTimeParticipationInCurrency: oneTimePayment * (si.participationInPercentage / 100),
                  periodicParticipationInCurrency: periodicPayment * (si.participationInPercentage / 100),
                })),
              },
            ]
          : undefined,
        investmentAmounts: [
          {
            payment: oneTimePayment,
            type: "OneTime",
          },
          {
            payment: periodicPayment,
            type: "Frequency",
          },
        ],
      },
    });
  }, [selection, analysisId, versionId, nsurePlan, mutate, isQuestionnaireLoading, isQuestionnaireNotFound]);

  const evaluationPlan = data?.evaluationInvestmentPlan;

  const planEvaluationMap = new Map(
    (evaluationPlan?.questionnaireEvaluationItems ?? []).map((item) => [item.evaluationType, item.planEvaluationType]),
  );
  const items = EVALUATION_ORDER.filter((type) => planEvaluationMap.has(type));
  const isOk = (type: PortfolioEvaluationItemEvaluationType) =>
    planEvaluationMap.get(type) === PortfolioEvaluationItemPlanEvaluationType.Match;

  return {
    evaluationPlan,
    items,
    okCount: items.filter(isOk).length,
    hasWarning: items.some((type) => !isOk(type)),
    isPending,
    isError,
    isDataStale,
    isQuestionnaireLoading,
    isQuestionnaireNotFound,
    isOk,
  };
};
